// =============================================================================
// API client — thin wrapper over fetch.
// In dev: requests go to /api which Vite proxies to localhost:8420.
// In prod: requests go to /api on the same origin (chat.doof.lol).
// =============================================================================

const STORAGE_TOKEN_KEY = 'chatapp_token';

export function getToken(): string | null {
  return localStorage.getItem(STORAGE_TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(STORAGE_TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(STORAGE_TOKEN_KEY);
}

// Called when the server rejects our token (revoked from another device,
// expired). App.tsx registers the sign-out; kept as a hook so api.ts stays
// free of React/store imports.
let onAuthLost: (() => void) | null = null;
export function setAuthLostHandler(fn: (() => void) | null): void {
  onAuthLost = fn;
}
/** Exact message requireAuth sends for a bad/revoked token — NOT any 401
 *  (a wrong password at login is also a 401). */
const AUTH_LOST_MESSAGE = 'Invalid or expired token';

export class ApiError extends Error {
  constructor(public status: number, message: string, public details?: unknown) {
    super(message);
  }
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  // For raw uploads (e.g., direct PUT to MinIO presigned URL)
  rawBody?: BodyInit;
  headers?: Record<string, string>;
  // Skip the auth header
  noAuth?: boolean;
  // Don't prefix with /api
  absolute?: boolean;
}

async function request<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const url = opts.absolute ? path : `/api${path}`;

  const headers: Record<string, string> = {
    ...(opts.body !== undefined && !opts.rawBody ? { 'Content-Type': 'application/json' } : {}),
    ...opts.headers,
  };

  if (!opts.noAuth) {
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.rawBody ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
  });

  if (res.status === 204) return undefined as T;

  // For non-API URLs (e.g. presigned uploads to MinIO), return raw response data
  if (opts.absolute) {
    return res as unknown as T;
  }

  let data: unknown;
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) {
    data = await res.json();
  } else {
    data = await res.text();
  }

  if (!res.ok) {
    if (
      res.status === 401 &&
      headers['Authorization'] &&
      data && typeof data === 'object' && (data as { error?: unknown }).error === AUTH_LOST_MESSAGE
    ) {
      onAuthLost?.();
    }
    const errMsg =
      (data && typeof data === 'object' && 'error' in data
        ? String((data as { error: unknown }).error)
        : null) ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, errMsg, data);
  }

  return data as T;
}

// =============================================================================
// API methods
// =============================================================================

import type {
  User, Server, Channel, ChannelCategory, Member, Message, VoiceParticipant, DmThread, UserSearchResult,
  AdminOverview, AdminUsersPage, AdminServersPage, AdminStorage, AdminActivity,
  AuditLogEntry, ServerEmoji, UnreadEntry, SystemSettings, GiphySlim, ActiveSession,
} from './types';

export const api = {
  // -------------- Auth --------------
  register: (input: { username: string; email: string; password: string; displayName?: string; inviteCode?: string }) =>
    request<{ user: User; token: string }>('/auth/register', {
      method: 'POST',
      body: input,
      noAuth: true,
    }),

  /** With two-factor on, a correct password returns `mfaRequired` + a
   *  short-lived `mfaToken` instead of a session; finish with loginTwoFactor. */
  login: (input: { identifier: string; password: string }) =>
    request<{ user: User; token: string } | { mfaRequired: true; mfaToken: string }>('/auth/login', {
      method: 'POST',
      body: input,
      noAuth: true,
    }),
  loginTwoFactor: (input: { mfaToken: string; code: string }) =>
    request<{ user: User; token: string; usedRecoveryCode?: boolean }>('/auth/login/2fa', {
      method: 'POST',
      body: input,
      noAuth: true,
    }),
  /** Download everything we hold for this account (JSON file). */
  exportAccountData: async (): Promise<{ blob: Blob; filename: string }> => {
    const res = await fetch('/api/auth/export', { headers: { Authorization: `Bearer ${getToken() ?? ''}` } });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      throw new ApiError(res.status, body?.error ?? `HTTP ${res.status}`, body);
    }
    const cd = res.headers.get('content-disposition') ?? '';
    const filename = /filename="([^"]+)"/.exec(cd)?.[1] ?? 'doof-export.json';
    return { blob: await res.blob(), filename };
  },
  deleteAccountBlockers: () =>
    request<{ ownedServers: { id: string; name: string }[] }>('/auth/delete-account/blockers'),
  deleteAccount: (input: { password: string; code?: string }) =>
    request<{ ok: true }>('/auth/delete-account', { method: 'POST', body: input }),
  twoFactorStatus: () =>
    request<{ enabled: boolean; enabledAt: string | null; recoveryCodesLeft: number }>('/auth/2fa'),
  twoFactorSetup: (password: string) =>
    request<{ secret: string; otpauthUrl: string }>('/auth/2fa/setup', { method: 'POST', body: { password } }),
  twoFactorEnable: (code: string) =>
    request<{ recoveryCodes: string[] }>('/auth/2fa/enable', { method: 'POST', body: { code } }),
  twoFactorDisable: (input: { password: string; code: string }) =>
    request<{ ok: true }>('/auth/2fa/disable', { method: 'POST', body: input }),
  twoFactorRegenerateCodes: (code: string) =>
    request<{ recoveryCodes: string[] }>('/auth/2fa/recovery-codes', { method: 'POST', body: { code } }),

  me: () => request<User>('/auth/me'),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  listSessions: () =>
    request<{ sessions: ActiveSession[]; currentIsLegacy: boolean }>('/auth/sessions'),
  revokeSession: (id: string) =>
    request<void>(`/auth/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  revokeOtherSessions: () =>
    request<{ ok: true; revoked: number; token: string }>('/auth/sessions/revoke-others', { method: 'POST' }),

  // -------------- Desktop update check --------------
  // Latest published desktop (Electron) version + download page. `version` is
  // null when the backend has no DESKTOP_LATEST_VERSION configured. Public
  // (no auth); only used by the desktop wrapper's update check.
  desktopVersion: () =>
    request<{ version: string | null; url: string; notes?: string }>('/desktop-version', {
      noAuth: true,
    }),

  updateProfile: (input: { displayName?: string | null; isInvisible?: boolean; dmPrivacy?: import('./types').DmPrivacy }) =>
    request<User>('/auth/me', { method: 'PATCH', body: input }),

  // Returns a fresh token: changing the password revokes all prior tokens
  // (including this device's), so the caller must swap to the returned one to
  // stay signed in.
  changePassword: (input: { currentPassword: string; newPassword: string }) =>
    request<{ ok: true; token?: string }>('/auth/change-password', { method: 'POST', body: input }),

  // -------------- Push notifications --------------
  getVapidKey: () =>
    request<{ enabled: boolean; publicKey: string | null }>('/push/vapid-public-key'),
  pushSubscribe: (subscription: unknown) =>
    request<{ ok: true }>('/push/subscribe', { method: 'POST', body: { subscription } }),
  pushUnsubscribe: (endpoint: string) =>
    request<{ ok: true }>('/push/unsubscribe', { method: 'POST', body: { endpoint } }),

  // -------------- Servers --------------
  listServers: () => request<Server[]>('/servers'),
  reorderServers: (orderedIds: string[]) =>
    request<{ ok: true }>('/servers/reorder', { method: 'PUT', body: { orderedIds } }),
  getServer: (id: string) => request<Server>(`/servers/${id}`),
  getServerByCode: (code: string) => request<Server>(`/servers/by-code/${code}`),
  /** Public (no login): what an /invite/:code link points at. */
  invitePreview: (code: string) =>
    request<{ name: string; iconUrl: string | null; memberCount: number }>(
      `/invites/${encodeURIComponent(code)}`,
      { noAuth: true }
    ),
  createServer: (input: { name: string; iconUrl?: string }) =>
    request<Server>('/servers', { method: 'POST', body: input }),
  updateServer: (id: string, input: { name?: string }) =>
    request<{ ok: true }>(`/servers/${id}`, { method: 'PATCH', body: input }),
  joinServer: (inviteCode: string) =>
    request<{ serverId: string }>('/servers/join', { method: 'POST', body: { inviteCode } }),
  leaveServer: (id: string) =>
    request<{ ok: true }>(`/servers/${id}/leave`, { method: 'POST' }),
  rotateInvite: (id: string) =>
    request<{ inviteCode: string }>(`/servers/${id}/rotate-invite`, { method: 'POST' }),

  // -------------- Custom emoji --------------
  listServerEmojis: (serverId: string) =>
    request<{ emojis: ServerEmoji[] }>(`/servers/${serverId}/emojis`),
  /** Custom emoji from every server the current user is in — used in DMs
   *  where there's no server scope. */
  listAllMyEmojis: () =>
    request<{ emojis: ServerEmoji[] }>(`/me/emojis`),
  createServerEmoji: (serverId: string, input: { name: string; storageKey: string }) =>
    request<ServerEmoji>(`/servers/${serverId}/emojis`, { method: 'POST', body: input }),
  deleteServerEmoji: (id: string) =>
    request<{ ok: true }>(`/emojis/${id}`, { method: 'DELETE' }),
  listMembers: (id: string) => request<Member[]>(`/servers/${id}/members`),
  /** Owner only: hand the server to another member (you become an admin). */
  transferServer: (serverId: string, userId: string) =>
    request<{ ok: true }>(`/servers/${serverId}/transfer`, { method: 'POST', body: { userId } }),
  changeRole: (serverId: string, userId: string, role: 'admin' | 'moderator' | 'member') =>
    request<{ ok: true }>(`/servers/${serverId}/members/${userId}`, {
      method: 'PATCH',
      body: { role },
    }),
  kickMember: (serverId: string, userId: string) =>
    request<{ ok: true }>(`/servers/${serverId}/members/${userId}`, { method: 'DELETE' }),

  // -------------- Avatars / Icons --------------
  presignImage: (input: { kind: 'avatar' | 'icon' | 'emoji' | 'profile-banner' | 'profile-background'; serverId?: string; contentType: string; sizeBytes: number }) =>
    request<{ storageKey: string; uploadUrl: string; contentType: string; expiresIn: number }>(
      '/uploads/presign-image',
      { method: 'POST', body: input }
    ),
  setMyAvatar: (storageKey: string) =>
    request<{ avatarUrl: string | null }>('/auth/me/avatar', {
      method: 'PUT',
      body: { storageKey },
    }),
  removeMyAvatar: () =>
    request<{ avatarUrl: string | null }>('/auth/me/avatar', { method: 'DELETE' }),
  setServerIcon: (serverId: string, storageKey: string) =>
    request<{ iconUrl: string | null }>(`/servers/${serverId}/icon`, {
      method: 'PUT',
      body: { storageKey },
    }),
  removeServerIcon: (serverId: string) =>
    request<{ iconUrl: string | null }>(`/servers/${serverId}/icon`, { method: 'DELETE' }),

  // -------------- DMs --------------
  listDms: () => request<DmThread[]>('/dms'),
  openDm: (input: { userId: string } | { username: string }) =>
    request<DmThread>('/dms', { method: 'POST', body: input }),
  searchUsers: (q: string) =>
    request<UserSearchResult[]>(`/users/search?q=${encodeURIComponent(q)}`),

  // -------------- Friends --------------
  getFriends: () => request<import('./types').FriendsList>('/friends'),
  friendStatus: (userId: string) =>
    request<import('./types').FriendStatusResponse>(`/friends/${userId}/status`),
  addFriend: (input: { userId: string } | { username: string }) =>
    request<{ status: 'pending' | 'accepted' }>('/friends/requests', { method: 'POST', body: input }),
  acceptFriend: (userId: string) =>
    request<{ status: 'accepted' }>(`/friends/requests/${userId}/accept`, { method: 'POST' }),
  removeFriendRequest: (userId: string) =>
    request<{ ok: true }>(`/friends/requests/${userId}`, { method: 'DELETE' }),
  unfriend: (userId: string) =>
    request<{ ok: true }>(`/friends/${userId}`, { method: 'DELETE' }),
  blockUser: (userId: string) =>
    request<{ ok: true }>(`/friends/blocks/${userId}`, { method: 'POST' }),
  unblockUser: (userId: string) =>
    request<{ ok: true }>(`/friends/blocks/${userId}`, { method: 'DELETE' }),

  // -------------- Public profiles --------------
  getUserProfile: (username: string) =>
    request<import('./types').PublicUserProfile>(`/users/${encodeURIComponent(username)}/profile`),
  updateMyProfile: (input: {
    bio?: string | null;
    pronouns?: string | null;
    customStatus?: string | null;
    links?: import('./types').ProfileLink[];
    accent?: string | null;
    visibility?: import('./types').ProfileVisibility;
  }) => request<{ ok: true }>('/users/me/profile', { method: 'PATCH', body: input }),
  setMyBanner: (storageKey: string) =>
    request<{ bannerUrl: string | null }>('/users/me/banner', { method: 'PUT', body: { storageKey } }),
  removeMyBanner: () =>
    request<{ bannerUrl: string | null }>('/users/me/banner', { method: 'DELETE' }),
  getMyTopFriends: () =>
    request<{ topFriends: import('./types').TopFriend[] }>('/users/me/top-friends'),
  setMyTopFriends: (friendIds: string[]) =>
    request<{ topFriends: import('./types').TopFriend[] }>('/users/me/top-friends', {
      method: 'PUT',
      body: { friendIds },
    }),
  getMyProfileCss: () => request<{ css: string }>('/users/me/profile-css'),
  setMyProfileCss: (css: string) =>
    request<{ sanitized: string; dropped: string[] }>('/users/me/profile-css', {
      method: 'PUT',
      body: { css },
    }),
  clearMyProfileCss: () => request<{ ok: true }>('/users/me/profile-css', { method: 'DELETE' }),
  setMyBackground: (storageKey: string) =>
    request<{ backgroundUrl: string | null }>('/users/me/background', { method: 'PUT', body: { storageKey } }),
  removeMyBackground: () =>
    request<{ backgroundUrl: string | null }>('/users/me/background', { method: 'DELETE' }),
  setMySong: (url: string) =>
    request<{ song: { provider: string | null; embedUrl: string; title: string | null } | null }>('/users/me/song', {
      method: 'PUT',
      body: { url },
    }),
  removeMySong: () =>
    request<{ song: null }>('/users/me/song', { method: 'DELETE' }),

  getGuestbook: (username: string, before?: string) => {
    const qs = before ? `?before=${encodeURIComponent(before)}` : '';
    return request<{ entries: import('./types').GuestbookEntry[] }>(
      `/users/${encodeURIComponent(username)}/guestbook${qs}`
    );
  },
  postGuestbook: (username: string, body: string) =>
    request<import('./types').GuestbookEntry>(
      `/users/${encodeURIComponent(username)}/guestbook`,
      { method: 'POST', body: { body } }
    ),
  deleteGuestbookEntry: (id: string) =>
    request<{ ok: true }>(`/guestbook/${id}`, { method: 'DELETE' }),

  // -------------- Notifications --------------
  getNotifications: (before?: string) => {
    const qs = before ? `?before=${encodeURIComponent(before)}` : '';
    return request<{ notifications: import('./types').Notification[]; unreadCount: number }>(
      `/notifications${qs}`
    );
  },
  markNotificationsRead: (id?: string) =>
    request<{ ok: true }>('/notifications/read', { method: 'POST', body: id ? { id } : {} }),

  // -------------- Public auth config --------------
  authConfig: () => request<{ registrationEnabled: boolean }>('/auth/config'),

  // -------------- Unread tracking --------------
  listUnreads: () => request<{ unreads: UnreadEntry[] }>('/unreads'),
  markChannelRead: (channelId: string) =>
    request<{ ok: true; advanced: boolean }>(`/channels/${channelId}/read`, {
      method: 'POST',
    }),

  // -------------- Reactions --------------
  addReaction: (messageId: string, emoji: string) =>
    request<{ ok: true }>(`/messages/${messageId}/reactions`, {
      method: 'POST',
      body: { emoji },
    }),
  removeReaction: (messageId: string, emoji: string) =>
    request<{ ok: true }>(
      `/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
      { method: 'DELETE' }
    ),
  listReactors: (messageId: string, emoji: string) =>
    request<{ reactors: Array<{ userId: string; username: string; displayName: string | null }> }>(
      `/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`
    ),

  // -------------- Admin --------------
  adminOverview: () => request<AdminOverview>('/admin/overview'),
  adminListUsers: (opts: { q?: string; page?: number; pageSize?: number }) => {
    const params = new URLSearchParams();
    if (opts.q) params.set('q', opts.q);
    if (opts.page) params.set('page', String(opts.page));
    if (opts.pageSize) params.set('pageSize', String(opts.pageSize));
    const qs = params.toString();
    return request<AdminUsersPage>(`/admin/users${qs ? '?' + qs : ''}`);
  },
  adminDeleteUser: (id: string) =>
    request<{ ok: true }>(`/admin/users/${id}`, { method: 'DELETE' }),
  adminSetSuperAdmin: (id: string, value: boolean) =>
    request<{ ok: true; isSuperAdmin: boolean }>(`/admin/users/${id}/super-admin`, {
      method: 'POST',
      body: { value },
    }),
  /** Issue a one-time password reset link for a user. Returns the raw
   *  token ONCE — admin must copy it now or issue another. */
  adminIssuePasswordReset: (id: string) =>
    request<{ token: string; username: string; expiresInHours: number }>(
      `/admin/users/${id}/password-reset`,
      { method: 'POST' }
    ),
  /** Last-resort: turn off a user's two-factor login (lost phone + codes). */
  adminDisableTwoFactor: (id: string) =>
    request<{ ok: true; username: string }>(`/admin/users/${id}/disable-2fa`, { method: 'POST' }),
  /** Validate a reset token (preview, no consume). Returns 404 wrapped
   *  as an ApiError on invalid/expired. */
  resetPasswordPreview: (token: string) =>
    request<{ username: string; expiresAt: string }>(
      `/auth/reset-password/${encodeURIComponent(token)}`
    ),
  /** Redeem a reset token + set a new password. */
  resetPassword: (input: { token: string; newPassword: string }) =>
    request<{ ok: true; username: string | null }>(`/auth/reset-password`, {
      method: 'POST',
      body: input,
    }),
  /** Request a self-service password-reset email. Always resolves ok (the
   *  server doesn't reveal whether the address matches an account). */
  forgotPassword: (email: string) =>
    request<{ ok: true }>(`/auth/forgot-password`, {
      method: 'POST',
      body: { email },
    }),
  /** Redeem an email-verification token. */
  verifyEmail: (token: string) =>
    request<{ ok: true; username: string | null }>(`/auth/verify-email`, {
      method: 'POST',
      body: { token },
    }),
  /** Re-send the verification email to the signed-in user. */
  resendVerification: () =>
    request<{ ok: true; alreadyVerified?: boolean }>(`/auth/resend-verification`, {
      method: 'POST',
    }),
  adminListServers: (opts: { q?: string; page?: number; pageSize?: number }) => {
    const params = new URLSearchParams();
    if (opts.q) params.set('q', opts.q);
    if (opts.page) params.set('page', String(opts.page));
    if (opts.pageSize) params.set('pageSize', String(opts.pageSize));
    const qs = params.toString();
    return request<AdminServersPage>(`/admin/servers${qs ? '?' + qs : ''}`);
  },
  adminDeleteServer: (id: string) =>
    request<{ ok: true; attachmentsCleaned: number }>(`/admin/servers/${id}`, {
      method: 'DELETE',
    }),
  adminStorage: () => request<AdminStorage>('/admin/storage'),
  adminActivity: () => request<AdminActivity>('/admin/activity'),
  adminSettings: () => request<SystemSettings>('/admin/settings'),
  adminUpdateSettings: (patch: Partial<SystemSettings>) =>
    request<SystemSettings>('/admin/settings', { method: 'PATCH', body: patch }),
  adminAuditLog: (opts?: { beforeId?: number; limit?: number; actorId?: string; action?: string }) => {
    const params = new URLSearchParams();
    if (opts?.beforeId) params.set('beforeId', String(opts.beforeId));
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.actorId) params.set('actorId', opts.actorId);
    if (opts?.action) params.set('action', opts.action);
    const qs = params.toString();
    return request<{ entries: AuditLogEntry[] }>(`/admin/audit-log${qs ? `?${qs}` : ''}`);
  },

  // -------------- Channels --------------
  listChannels: (serverId: string) =>
    request<Channel[]>(`/servers/${serverId}/channels`),
  createChannel: (serverId: string, input: { name: string; type: 'text' | 'voice'; topic?: string }) =>
    request<Channel>(`/servers/${serverId}/channels`, { method: 'POST', body: input }),
  deleteChannel: (id: string) =>
    request<{ ok: true }>(`/channels/${id}`, { method: 'DELETE' }),
  reorderChannels: (serverId: string, input: { type: 'text' | 'voice'; ids: string[] }) =>
    request<{ ok: true }>(`/servers/${serverId}/channels/order`, {
      method: 'PATCH',
      body: input,
    }),
  // Move a channel into a category (or to uncategorized with categoryId=null).
  // orderedIds is the full ordered id list the destination should contain.
  moveChannel: (serverId: string, input: { categoryId: string | null; orderedIds: string[] }) =>
    request<{ ok: true }>(`/servers/${serverId}/channels/move`, { method: 'PUT', body: input }),

  // -------------- Channel categories --------------
  listCategories: (serverId: string) =>
    request<ChannelCategory[]>(`/servers/${serverId}/categories`),
  createCategory: (serverId: string, name: string) =>
    request<ChannelCategory>(`/servers/${serverId}/categories`, { method: 'POST', body: { name } }),
  renameCategory: (id: string, name: string) =>
    request<ChannelCategory>(`/categories/${id}`, { method: 'PATCH', body: { name } }),
  deleteCategory: (id: string) =>
    request<{ ok: true }>(`/categories/${id}`, { method: 'DELETE' }),
  reorderCategories: (serverId: string, ids: string[]) =>
    request<{ ok: true }>(`/servers/${serverId}/categories/reorder`, { method: 'PUT', body: { ids } }),

  // -------------- Messages --------------
  listMessages: (channelId: string, opts?: { limit?: number; before?: string }) => {
    const params = new URLSearchParams();
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.before) params.set('before', opts.before);
    const qs = params.toString();
    return request<{ messages: Message[]; hasMore: boolean }>(
      `/channels/${channelId}/messages${qs ? '?' + qs : ''}`
    );
  },
  searchMessages: (
    channelId: string,
    opts: { text?: string; from?: string; has?: 'link' | 'file' | 'image'; limit?: number; offset?: number }
  ) => {
    const params = new URLSearchParams();
    if (opts.text) params.set('text', opts.text);
    if (opts.from) params.set('from', opts.from);
    if (opts.has) params.set('has', opts.has);
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.offset) params.set('offset', String(opts.offset));
    return request<{ messages: Message[]; hasMore: boolean }>(
      `/channels/${channelId}/messages/search?${params.toString()}`
    );
  },
  sendMessage: (channelId: string, content: string, replyToId?: string) =>
    request<Message>(`/channels/${channelId}/messages`, {
      method: 'POST',
      body: { content, replyToId },
    }),
  editMessage: (id: string, content: string) =>
    request<Message>(`/messages/${id}`, { method: 'PATCH', body: { content } }),
  deleteMessage: (id: string) =>
    request<{ ok: true }>(`/messages/${id}`, { method: 'DELETE' }),
  pinMessage: (id: string) =>
    request<{ ok: true }>(`/messages/${id}/pin`, { method: 'PUT' }),
  unpinMessage: (id: string) =>
    request<{ ok: true }>(`/messages/${id}/pin`, { method: 'DELETE' }),
  getChannelPins: (channelId: string) =>
    request<{ messages: Message[] }>(`/channels/${channelId}/pins`),

  // -------------- Uploads --------------
  presignUpload: (input: {
    channelId: string;
    filename: string;
    contentType: string;
    sizeBytes: number;
    width?: number;
    height?: number;
  }) =>
    request<{ storageKey: string; uploadUrl: string; contentType: string; expiresIn: number }>(
      '/uploads/presign',
      { method: 'POST', body: input }
    ),

  uploadDirect: async (uploadUrl: string, file: File) => {
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!res.ok) throw new Error(`Upload failed: HTTP ${res.status}`);
  },

  finalizeUpload: (input: {
    channelId: string;
    uploads: Array<{
      storageKey: string;
      filename: string;
      contentType: string;
      width?: number;
      height?: number;
    }>;
    content?: string;
    replyToId?: string;
  }) =>
    request<Message>('/uploads/finalize', { method: 'POST', body: input }),

  attachmentUrl: (attachmentId: string) =>
    request<{ url: string; filename: string; contentType: string; expiresIn: number }>(
      `/attachments/${attachmentId}/url`
    ),

  // -------------- GIFs (Giphy) --------------
  /** List trending GIFs from Giphy. Returns 503 if backend has no key. */
  gifsTrending: () =>
    request<{ data: GiphySlim[] }>('/gifs/trending'),
  /** Search Giphy for the given query. */
  gifsSearch: (q: string, offset?: number) => {
    const params = new URLSearchParams({ q });
    if (offset) params.set('offset', String(offset));
    return request<{ data: GiphySlim[] }>(`/gifs/search?${params.toString()}`);
  },
  /** Stage a chosen GIF as an attachment. Server downloads from Giphy
   *  and stores in MinIO; returns the storage key + metadata so the
   *  client can add it to pendingAttachments. The user then sends the
   *  message normally (with optional caption text). */
  gifsStage: (input: { fullUrl: string }) =>
    request<{
      storageKey: string;
      filename: string;
      contentType: string;
      sizeBytes: number;
      width: number | null;
      height: number | null;
      previewUrl: string;
    }>('/gifs/stage', { method: 'POST', body: input }),

  // -------------- Voice --------------
  voiceToken: (channelId: string, opts?: { popout?: boolean }) =>
    request<{ url: string; token: string; roomName: string; expiresAt: number; serverMuted?: boolean }>(
      '/voice/token',
      { method: 'POST', body: { channelId, ...(opts?.popout ? { popout: true } : {}) } }
    ),

  voiceParticipants: (channelId: string) =>
    request<VoiceParticipant[]>(`/voice/${channelId}/participants`),

  // Snapshot of who is currently in any voice call → { userId: channelId }.
  activeVoiceUsers: () =>
    request<{ active: Record<string, string>; serverMuted?: Record<string, string[]> }>('/voice/active'),
  // Moderator voice controls (moderate_voice + must outrank the target).
  setVoiceServerMute: (serverId: string, userId: string, muted: boolean) =>
    request<{ ok: true; muted: boolean }>(`/voice/servers/${serverId}/members/${userId}/mute`, {
      method: muted ? 'PUT' : 'DELETE',
    }),
  disconnectFromVoice: (channelId: string, userId: string) =>
    request<{ ok: true }>(`/voice/channels/${channelId}/members/${userId}/disconnect`, {
      method: 'POST',
    }),

  // Bulk fetch: { channelId: [participants...] } for all voice channels in a server
  serverVoiceState: (serverId: string) =>
    request<Record<string, VoiceParticipant[]>>(`/servers/${serverId}/voice-state`),

  // -------------- Instant meeting rooms --------------
  // Creating and managing a room is authenticated; joining one is not. The
  // chat calls authenticate with a room-scoped meet token in its own header
  // rather than the app's Bearer token — see services/meetAuth.ts for why the
  // two credential types are deliberately kept apart.

  createMeetRoom: (name?: string) =>
    request<MeetRoomSummary>('/meet', { method: 'POST', body: name ? { name } : {} }),

  myMeetRooms: () => request<{ rooms: MeetRoomSummary[] }>('/meet/mine'),

  endMeetRoom: (code: string) => request<void>(`/meet/${code}`, { method: 'DELETE' }),

  setMeetLocked: (code: string, locked: boolean) =>
    request<MeetRoomSummary>(`/meet/${code}`, { method: 'PATCH', body: { locked } }),

  // Pre-join card. `noAuth` so a signed-out visitor isn't sent a stale token.
  meetInfo: (code: string) =>
    request<MeetInfo>(`/meet/${code}`, { noAuth: true }),

  // Anonymous join. Returns LiveKit media credentials plus the meet token.
  meetJoin: (code: string, displayName: string) =>
    request<MeetCredentials>(`/meet/${code}/join`, {
      method: 'POST',
      body: { displayName },
      noAuth: true,
    }),

  // Signed-in join — same room, but you appear as yourself.
  meetHostToken: (code: string) =>
    request<MeetCredentials & { isHost: boolean }>(`/meet/${code}/host-token`, {
      method: 'POST',
    }),

  meetChatList: (code: string, meetToken: string) =>
    request<{ messages: MeetChatMessage[] }>(`/meet/${code}/chat`, {
      noAuth: true,
      headers: { 'X-Meet-Token': meetToken },
    }),

  meetChatSend: (code: string, meetToken: string, text: string) =>
    request<MeetChatMessage>(`/meet/${code}/chat`, {
      method: 'POST',
      body: { text },
      noAuth: true,
      headers: { 'X-Meet-Token': meetToken },
    }),
};

export interface MeetRoomSummary {
  code: string;
  name: string;
  locked: boolean;
  createdAt: number;
  expiresAt: number;
  url: string;
  participants?: number;
}

export interface MeetInfo {
  code: string;
  name: string;
  hostName: string;
  expiresAt: number;
  locked: boolean;
  full: boolean;
  participants: number;
}

export interface MeetCredentials {
  livekit: { url: string; token: string; roomName: string };
  meetToken: string;
  identity: string;
  room: { code: string; name: string; hostName: string; expiresAt: number; locked: boolean };
}

export interface MeetChatMessage {
  id: string;
  from: string;
  name: string;
  guest: boolean;
  text: string;
  at: number;
}
